/**
 * Build Handoff Dialog
 *
 * Lets the user collaborate with the AI to turn a winning proposal into a
 * precise BUILD SPECIFICATION + ready-to-paste prompts for EXTERNAL AI coding
 * agents (Claude Code, Cursor, etc.) that will scaffold the actual product.
 *
 * Flow: chat to refine intent/architecture -> "Generate Build Brief" produces a
 * structured markdown brief + agent prompts + open questions, all copyable and
 * downloadable. Every call is user-initiated (no loops).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Send, Sparkles, Copy, Check, Download, Loader2, FileCode2, MessageSquare, HelpCircle,
  Paperclip, FileText, Save, ScrollText, History, Trash2, Pencil, Eye, RotateCcw,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { Contract } from '../../types';

interface ChatMsg { role: 'user' | 'assistant'; content: string; }
interface AgentPrompt { title: string; prompt: string; }
interface Attachment { name: string; text: string; status: 'extracting' | 'ready' | 'error'; }
interface HandoffVersion {
  id: string;
  label: string | null;
  brief_markdown: string | null;
  agent_prompts: AgentPrompt[];
  open_questions: string[];
  intention: string | null;
  created_at: string;
}

interface BuildHandoffDialogProps {
  proposal: any;          // content_json
  contract: Contract;
  onClose: () => void;
}

function asText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '\n…[truncated]' : s;
}

function humanizeKey(k: string): string {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Render an arbitrary nested value (string / array / object) as compact, readable
// markdown bullets. Far more token-efficient than JSON.stringify and — crucially —
// loses NOTHING, so every synthesized detail (features, screens, journeys, roles,
// SLAs, phases, risks…) survives the handoff instead of being JSON-dumped + chopped.
function renderValue(v: any, indent: string): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    return `${indent}${v}`;
  }
  if (Array.isArray(v)) {
    return v
      .map((item) => {
        if (item == null || item === '') return '';
        if (typeof item === 'object') {
          const inner = renderObject(item, indent + '  ');
          return inner ? `${indent}-\n${inner}` : '';
        }
        return `${indent}- ${item}`;
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof v === 'object') return renderObject(v, indent);
  return '';
}

function renderObject(obj: any, indent: string): string {
  const lines: string[] = [];
  for (const [k, val] of Object.entries(obj)) {
    if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) continue;
    const label = humanizeKey(k);
    if (typeof val === 'object') {
      const rendered = renderValue(val, indent + '  ');
      if (rendered.trim()) lines.push(`${indent}- **${label}:**\n${rendered}`);
    } else {
      lines.push(`${indent}- **${label}:** ${val}`);
    }
  }
  return lines.join('\n');
}

// Turn the (now rich) synthesized product definition into a complete, readable
// markdown spec — every section, nothing dropped — so the Build Handoff is grounded
// in the FULL product, not a 5k-char raw-JSON stub.
function formatProductDefinition(pd: any): string {
  if (!pd || typeof pd !== 'object') return asText(pd);
  const out: string[] = [];
  for (const [key, val] of Object.entries(pd)) {
    if (key === 'metadata') continue; // synthesis bookkeeping, not build content
    if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) continue;
    const rendered =
      typeof val === 'object' ? renderValue(val, '') : `${val}`;
    if (!rendered.trim()) continue;
    out.push(`### ${humanizeKey(key)}\n${rendered}`);
  }
  return out.join('\n\n');
}

// Build a compact, grounded context string from the proposal + contract.
function buildContext(content: any, contract: Contract): string {
  const parts: string[] = [];
  parts.push(
    `# SOLICITATION\n` +
    `Title: ${contract.title || 'n/a'}\n` +
    `Agency: ${contract.agency || 'n/a'}\n` +
    `Notice ID: ${(contract as any).notice_id || 'n/a'}\n` +
    `NAICS: ${(contract as any).naics_code || 'n/a'}\n` +
    `Set-aside: ${(contract as any).set_aside || (contract as any).type_of_set_aside || 'n/a'}`
  );
  if ((contract as any).description) {
    parts.push(`## SOLICITATION DESCRIPTION\n${truncate(asText((contract as any).description), 4000)}`);
  }
  if (content?.contractAnalysis) {
    parts.push(`## CONTRACT ANALYSIS\n${truncate(asText(content.contractAnalysis), 4000)}`);
  }
  (content?.technicalSections || []).forEach((s: any) => {
    parts.push(`## PROPOSAL SECTION: ${s.title}\n${truncate(asText(s.content), 3000)}`);
  });
  const dl = content?.deliverablesData;
  if (dl?.deliverables_with_labor?.length) {
    parts.push(
      `## DELIVERABLES\n` +
      dl.deliverables_with_labor
        .map((d: any) => {
          const name = d.name || d.deliverable_name || d.title || 'Deliverable';
          const meta = [d.type, d.quantity ? `qty ${d.quantity}${d.unit ? ' ' + d.unit : ''}` : null]
            .filter(Boolean).join(', ');
          const desc = truncate(asText(d.description || d.specifications || ''), 600);
          return `- **${name}**${meta ? ` (${meta})` : ''}${desc ? `: ${desc}` : ''}`;
        })
        .join('\n')
    );
  }
  // The synthesized Product is the richest source of truth for the build — render
  // it in FULL (every feature, screen, journey, role, SLA, phase, risk…), not a
  // 5k-char raw-JSON stub. It sits early in the context so it survives the edge cap.
  if (dl?.product_definition) {
    parts.push(`## PRODUCT / SOLUTION DEFINITION\n${truncate(formatProductDefinition(dl.product_definition), 40000)}`);
  }
  return parts.join('\n\n');
}

export function BuildHandoffDialog({ proposal, contract, onClose }: BuildHandoffDialogProps) {
  const proposalContext = useMemo(() => buildContext(proposal, contract), [proposal, contract]);
  const noticeId = (contract as any)?.notice_id || (contract as any)?.noticeId || null;

  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [brief, setBrief] = useState<string | null>(null);
  const [agentPrompts, setAgentPrompts] = useState<AgentPrompt[]>([]);
  const [openQuestions, setOpenQuestions] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [tab, setTab] = useState<'chat' | 'brief'>('chat');
  const [solicitationDocs, setSolicitationDocs] = useState<{ name: string; text: string }[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Persistence: a saved handoff per (user, contract) the user can reopen, regenerate,
  // and iterate on — plus an authored "intention / build constitution" that steers
  // every generation. The chat history is saved too so collaboration resumes later.
  const [profileId, setProfileId] = useState<string | null>(null);
  const [handoffId, setHandoffId] = useState<string | null>(null);
  const [buildMode, setBuildMode] = useState<'demo' | 'production'>('demo');
  const [intention, setIntention] = useState('');
  const [showIntention, setShowIntention] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [editingBrief, setEditingBrief] = useState(false);
  const [versions, setVersions] = useState<HandoffVersion[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  // Latest values for save (avoids stale closures when persisting right after setState).
  const latest = useRef({ intention: '', brief: null as string | null, agentPrompts: [] as AgentPrompt[], openQuestions: [] as string[], messages: [] as ChatMsg[], buildMode: 'demo' as 'demo' | 'production' });
  latest.current = { intention, brief, agentPrompts, openQuestions, messages, buildMode };

  // Load this user's saved handoff for this contract (if any) on open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        const pid = userData?.user?.id || null;
        if (cancelled) return;
        setProfileId(pid);
        if (!pid || !noticeId) { setLoadingSaved(false); return; }
        const { data, error } = await supabase
          .from('build_handoffs')
          .select('id, build_mode, intention, brief_markdown, agent_prompts, open_questions, chat_messages, updated_at')
          .eq('profile_id', pid)
          .eq('notice_id', noticeId)
          .maybeSingle();
        if (!cancelled && !error && data) {
          setHandoffId(data.id || null);
          if (data.build_mode === 'production' || data.build_mode === 'demo') setBuildMode(data.build_mode);
          setIntention(data.intention || '');
          if (data.intention) setShowIntention(true);
          if (data.brief_markdown) setBrief(data.brief_markdown);
          if (Array.isArray(data.agent_prompts)) setAgentPrompts(data.agent_prompts as AgentPrompt[]);
          if (Array.isArray(data.open_questions)) setOpenQuestions(data.open_questions as string[]);
          if (Array.isArray(data.chat_messages)) setMessages(data.chat_messages as ChatMsg[]);
          setSavedAt(data.updated_at || null);
          if (data.id) loadVersions(data.id);
        }
      } catch (e) {
        console.warn('Build Handoff: could not load saved handoff', e);
      } finally {
        if (!cancelled) setLoadingSaved(false);
      }
    })();
    return () => { cancelled = true; };
  }, [noticeId]);

  // Upsert the current handoff state. Overrides let callers persist fresh values
  // immediately after a generate/chat without waiting for a state re-render.
  const saveHandoff = async (
    overrides?: Partial<typeof latest.current>,
    opts?: { silent?: boolean },
  ): Promise<string | null> => {
    if (!profileId || !noticeId) {
      if (!opts?.silent) toast.error('Sign in to save this handoff.');
      return null;
    }
    const cur = { ...latest.current, ...overrides };
    if (!opts?.silent) setSaving(true);
    try {
      const { data, error } = await supabase
        .from('build_handoffs')
        .upsert({
          profile_id: profileId,
          notice_id: noticeId,
          contract_title: contract.title || null,
          build_mode: cur.buildMode || 'demo',
          intention: cur.intention || null,
          brief_markdown: cur.brief || null,
          agent_prompts: cur.agentPrompts || [],
          open_questions: cur.openQuestions || [],
          chat_messages: cur.messages || [],
        }, { onConflict: 'profile_id,notice_id' })
        .select('id')
        .single();
      if (error) throw error;
      if (data?.id) setHandoffId(data.id);
      setSavedAt(new Date().toISOString());
      setDirty(false);
      if (!opts?.silent) toast.success('Handoff saved.');
      return data?.id || handoffId;
    } catch (e: any) {
      console.error('Build Handoff save failed:', e);
      if (!opts?.silent) toast.error('Save failed. Try again.');
      return null;
    } finally {
      if (!opts?.silent) setSaving(false);
    }
  };

  // --- Version history -------------------------------------------------------
  const loadVersions = async (hid: string) => {
    try {
      const { data, error } = await supabase
        .from('build_handoff_versions')
        .select('id, label, brief_markdown, agent_prompts, open_questions, intention, created_at')
        .eq('handoff_id', hid)
        .order('created_at', { ascending: false });
      if (!error && Array.isArray(data)) setVersions(data as HandoffVersion[]);
    } catch (e) {
      console.warn('Build Handoff: could not load versions', e);
    }
  };

  // Snapshot the given artifact into version history (immutable). Called on each
  // generate so a prior brief is never lost when regenerating.
  const snapshotVersion = async (
    hid: string,
    snap: { brief: string | null; agentPrompts: AgentPrompt[]; openQuestions: string[]; intention: string; label?: string },
  ) => {
    if (!profileId || !hid || !snap.brief) return;
    try {
      const { error } = await supabase.from('build_handoff_versions').insert({
        handoff_id: hid,
        profile_id: profileId,
        label: snap.label || `Generated ${new Date().toLocaleString()}`,
        brief_markdown: snap.brief,
        agent_prompts: snap.agentPrompts || [],
        open_questions: snap.openQuestions || [],
        intention: snap.intention || null,
      });
      if (!error) loadVersions(hid);
    } catch (e) {
      console.warn('Build Handoff: could not snapshot version', e);
    }
  };

  const restoreVersion = (v: HandoffVersion) => {
    setBrief(v.brief_markdown || '');
    setAgentPrompts(Array.isArray(v.agent_prompts) ? v.agent_prompts : []);
    setOpenQuestions(Array.isArray(v.open_questions) ? v.open_questions : []);
    if (v.intention != null) setIntention(v.intention);
    setEditingBrief(false);
    setShowVersions(false);
    setTab('brief');
    setDirty(true); // user can Save to make the restored version current
    toast.success('Version restored — Save to keep it as the current handoff.');
  };

  const deleteVersion = async (id: string) => {
    try {
      const { error } = await supabase.from('build_handoff_versions').delete().eq('id', id);
      if (error) throw error;
      setVersions((prev) => prev.filter((v) => v.id !== id));
    } catch (e) {
      console.error('Build Handoff: delete version failed', e);
      toast.error('Could not delete version.');
    }
  };

  // --- Inline editing of agent prompts ---------------------------------------
  const updatePrompt = (i: number, patch: Partial<AgentPrompt>) => {
    setAgentPrompts((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
    setDirty(true);
  };
  const removePrompt = (i: number) => {
    setAgentPrompts((prev) => prev.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const addPrompt = () => {
    setAgentPrompts((prev) => [...prev, { title: 'New prompt', prompt: '' }]);
    setDirty(true);
  };

  // Pull the ACTUAL solicitation source documents (PWS/SOW/attachments) so the AI
  // grounds the build spec in the real requirements, not just the proposal narrative.
  useEffect(() => {
    if (!noticeId) return;
    let cancelled = false;
    (async () => {
      setDocsLoading(true);
      try {
        const { data, error } = await supabase
          .from('contract_documents')
          .select('document_name, raw_text')
          .eq('contract_id', noticeId)
          .not('raw_text', 'is', null);
        if (!error && data && !cancelled) {
          setSolicitationDocs(
            data
              .filter((d: any) => typeof d.raw_text === 'string' && d.raw_text.trim().length > 200)
              .map((d: any) => ({ name: d.document_name || 'Document', text: d.raw_text as string })),
          );
        }
      } catch (e) {
        console.warn('Build Handoff: could not load solicitation documents', e);
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [noticeId]);

  // Assemble the full grounded context: proposal narrative + real solicitation
  // documents + any user-attached docs. Bounded per-source so one huge file can't
  // crowd out the others; the edge function applies a final overall cap.
  const buildFullContext = (): string => {
    const parts: string[] = [];
    if (intention.trim()) {
      parts.push(
        `# BUILD INTENTION & CONSTITUTION (AUTHORITATIVE — the user's guiding principles for this build; honor them over any default assumption)\n\n${intention.trim()}`,
      );
    }
    parts.push(proposalContext);
    const docsText = solicitationDocs
      .map((d) => `## SOLICITATION DOCUMENT: ${d.name}\n${truncate(d.text, 30000)}`)
      .join('\n\n');
    if (docsText) parts.push(`# SOLICITATION SOURCE DOCUMENTS (authoritative requirements)\n\n${docsText}`);
    const attachText = attachments
      .filter((a) => a.status === 'ready' && a.text.trim())
      .map((a) => `## ATTACHED DOCUMENT: ${a.name}\n${truncate(a.text, 30000)}`)
      .join('\n\n');
    if (attachText) parts.push(`# USER-ATTACHED DOCUMENTS\n\n${attachText}`);
    return parts.join('\n\n');
  };

  const TEXT_EXTS = ['txt', 'md', 'markdown', 'csv', 'json', 'log', 'rtf', 'text'];

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = Array.from(e.target.files || []);
    if (e.target) e.target.value = ''; // allow re-selecting the same file
    if (!fileList.length) return;
    setUploading(true);
    for (const file of fileList) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      setAttachments((prev) => [...prev, { name: file.name, text: '', status: 'extracting' }]);
      const mark = (status: Attachment['status'], text = '') =>
        setAttachments((prev) => prev.map((a) => (a.name === file.name && a.status === 'extracting' ? { ...a, status, text } : a)));
      try {
        if (TEXT_EXTS.includes(ext) || file.type.startsWith('text/')) {
          mark('ready', await file.text());
        } else if (noticeId) {
          // Binary (PDF/DOCX/XLSX): upload to storage + server-side extraction.
          const safe = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const path = `${noticeId}/handoff/${Date.now()}-${safe}`;
          const { error: upErr } = await supabase.storage.from('contract-documents').upload(path, file, { upsert: false });
          if (upErr) throw upErr;
          const { data: urlData } = supabase.storage.from('contract-documents').getPublicUrl(path);
          const { data: doc, error: dbErr } = await supabase.from('contract_documents').insert({
            contract_id: noticeId, document_name: file.name, document_url: urlData.publicUrl,
            document_type: 'handoff-attachment', file_type: file.type?.split('/')[1] || ext || 'pdf',
            file_size: file.size, status: 'pending',
          }).select('id').single();
          if (dbErr || !doc) throw (dbErr || new Error('Could not register document'));
          const { data: extracted, error: exErr } = await supabase.functions.invoke('extract-document-text', {
            body: { documentId: doc.id },
          });
          if (exErr) throw exErr;
          const text = extracted?.extractedText || extracted?.raw_text || '';
          if (!text) throw new Error('No text extracted');
          mark('ready', text);
        } else {
          throw new Error('Cannot extract this file type without a contract context');
        }
      } catch (err: any) {
        console.warn('Attachment extraction failed:', err);
        toast.error(`Could not read ${file.name}. Try a .txt/.md or paste the text.`);
        mark('error');
      }
    }
    setUploading(false);
  };

  const removeAttachment = (name: string) =>
    setAttachments((prev) => prev.filter((a) => a.name !== name));

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      toast.error('Copy failed — select and copy manually.');
    }
  };

  const sendChat = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('build-handoff-brief', {
        body: { mode: 'chat', context: buildFullContext(), messages: next, mode_variant: buildMode },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Request failed');
      const withReply = [...next, { role: 'assistant' as const, content: data.reply || '(no response)' }];
      setMessages(withReply);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
      // Persist the collaboration so it resumes next time the handoff is opened.
      saveHandoff({ messages: withReply }, { silent: true });
    } catch (e: any) {
      console.error('handoff chat error:', e);
      toast.error(e?.message?.includes('credit') ? 'AI unavailable: Anthropic credit balance too low.' : 'Chat failed. Try again.');
      setMessages(next); // keep the user message
    } finally {
      setSending(false);
    }
  };

  const applyResult = async (result: any) => {
    const newBrief = result.brief_markdown || '';
    const newPrompts = Array.isArray(result.agent_prompts) ? result.agent_prompts : [];
    const newQuestions = Array.isArray(result.open_questions) ? result.open_questions : [];
    setBrief(newBrief);
    setAgentPrompts(newPrompts);
    setOpenQuestions(newQuestions);
    setEditingBrief(false);
    setTab('brief');
    if (result.warning) toast('Generated, but formatting was imperfect.', { icon: '⚠️' });
    else toast.success('Build Brief generated.');
    // Auto-save the generated artifact so it's never lost on close, then snapshot it
    // into version history so regenerating never destroys a prior brief.
    const hid = await saveHandoff({ brief: newBrief, agentPrompts: newPrompts, openQuestions: newQuestions }, { silent: true });
    if (hid) {
      await snapshotVersion(hid, {
        brief: newBrief, agentPrompts: newPrompts, openQuestions: newQuestions, intention: latest.current.intention,
      });
    }
  };

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      // If the user has unsaved manual edits to the current brief, snapshot them BEFORE
      // regenerating overwrites them — so no manual work is lost to a regenerate.
      if (dirty && latest.current.brief && handoffId) {
        await snapshotVersion(handoffId, {
          brief: latest.current.brief,
          agentPrompts: latest.current.agentPrompts,
          openQuestions: latest.current.openQuestions,
          intention: latest.current.intention,
          label: `Edited (pre-regenerate) ${new Date().toLocaleString()}`,
        });
      }
      // Generation is large and exceeds the 150s synchronous gateway wall, so the edge
      // function runs it as a background job (202) and we poll the result row.
      const { data: userData } = await supabase.auth.getUser();
      const profileId = userData?.user?.id;
      const jobId = (crypto as any)?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const fullContext = buildFullContext();
      const { data, error } = await supabase.functions.invoke('build-handoff-brief', {
        body: profileId
          ? { mode: 'generate', context: fullContext, messages, job_id: jobId, profile_id: profileId, mode_variant: buildMode }
          : { mode: 'generate', context: fullContext, messages, mode_variant: buildMode }, // sync fallback if not signed in
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Generation failed');

      // Sync path (no profile / fallback): result is inline.
      if (!data?.async) {
        applyResult(data);
        return;
      }

      // Async path: poll build_handoff_jobs until done/error (cap ~7 min, above the
      // edge background-task budget so a slow-but-completing job isn't abandoned).
      const deadline = Date.now() + 7 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) throw new Error('Generation timed out. Please try again.');
        await new Promise((r) => setTimeout(r, 3000));
        const { data: job, error: pollErr } = await supabase
          .from('build_handoff_jobs')
          .select('status, result, error')
          .eq('id', jobId)
          .maybeSingle();
        if (pollErr) continue; // transient — keep polling
        if (job?.status === 'done') { applyResult(job.result || {}); return; }
        if (job?.status === 'error') throw new Error(job.error || 'Generation failed');
      }
    } catch (e: any) {
      console.error('handoff generate error:', e);
      toast.error(e?.message?.includes('credit') ? 'AI unavailable: Anthropic credit balance too low.' : (e?.message || 'Generation failed. Try again.'));
    } finally {
      setGenerating(false);
    }
  };

  const downloadBrief = () => {
    if (!brief) return;
    const promptsMd = agentPrompts.length
      ? '\n\n---\n\n# Agent Prompts\n\n' +
        agentPrompts.map((p, i) => `## ${i + 1}. ${p.title}\n\n\`\`\`\n${p.prompt}\n\`\`\``).join('\n\n')
      : '';
    const questionsMd = openQuestions.length
      ? '\n\n---\n\n# Open Questions\n\n' + openQuestions.map((q) => `- ${q}`).join('\n')
      : '';
    const blob = new Blob([brief + promptsMd + questionsMd], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(contract.title || 'proposal').replace(/[^a-z0-9]/gi, '_').slice(0, 50)}_build_brief.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-bw-surface dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl h-[88vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-bw-border dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileCode2 className="h-5 w-5 text-bw-primary" />
            <div>
              <h2 className="text-base font-bold text-bw-ink dark:text-white">Build Handoff</h2>
              <p className="text-xs text-bw-muted">Turn this proposal into a spec + prompts for your external AI builders</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {savedAt && !dirty && (
              <span className="text-xs text-bw-muted hidden sm:inline">
                Saved {new Date(savedAt).toLocaleString()}
              </span>
            )}
            {dirty && <span className="text-xs text-fit-moderate hidden sm:inline">Unsaved changes</span>}
            <button
              onClick={() => saveHandoff()}
              disabled={saving || loadingSaved}
              className="px-3 py-1.5 text-xs font-medium border border-bw-border dark:border-gray-600 rounded-lg flex items-center gap-1.5 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 disabled:opacity-50"
              title="Save this handoff (brief, prompts, intention & chat)"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </button>
            <button onClick={onClose} className="p-2 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 rounded-lg">
              <X className="h-5 w-5 text-bw-muted" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-bw-border dark:border-gray-700 text-sm">
          <button
            onClick={() => setTab('chat')}
            className={`px-4 py-2 font-medium flex items-center gap-1.5 ${tab === 'chat' ? 'text-bw-primary border-b-2 border-indigo-600' : 'text-bw-muted'}`}
          >
            <MessageSquare className="h-4 w-4" /> Refine
          </button>
          <button
            onClick={() => setTab('brief')}
            className={`px-4 py-2 font-medium flex items-center gap-1.5 ${tab === 'brief' ? 'text-bw-primary border-b-2 border-indigo-600' : 'text-bw-muted'}`}
          >
            <FileCode2 className="h-4 w-4" /> Build Brief {brief ? '' : '(empty)'}
          </button>
        </div>

        {/* Build mode selector */}
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-bw-border dark:border-gray-700 bg-bw-surface-subtle dark:bg-gray-950">
          <div className="inline-flex rounded-lg border border-bw-border dark:border-gray-600 overflow-hidden text-xs">
            {(['demo', 'production'] as const).map((m) => (
              <button
                key={m}
                onClick={() => { if (m !== buildMode) { setBuildMode(m); setDirty(true); saveHandoff({ buildMode: m }, { silent: true }); } }}
                className={`px-3 py-1.5 font-medium ${buildMode === m ? 'bg-bw-primary text-white' : 'bg-bw-surface dark:bg-gray-900 text-bw-ink dark:text-gray-200 hover:bg-bw-surface-subtle dark:hover:bg-gray-800'}`}
              >
                {m === 'demo' ? '✨ Demo build' : '🏗 Production build'}
              </button>
            ))}
          </div>
          <span className="text-xs text-bw-muted hidden sm:inline">
            {buildMode === 'demo'
              ? 'Deployable, beautiful showcase demo for your submission link'
              : 'Full build spec for the actual contract system'}
          </span>
        </div>

        {/* Body */}
        {tab === 'chat' ? (
          <>
            {/* Build Intention & Constitution — authored guidance fed into every generation */}
            <div className="border-b border-bw-border dark:border-gray-700">
              <button
                onClick={() => setShowIntention((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-bw-surface-subtle dark:hover:bg-gray-800"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-bw-ink dark:text-gray-100">
                  <ScrollText className="h-4 w-4 text-bw-primary" />
                  Build Intention &amp; Constitution
                  {intention.trim() && <span className="text-xs text-success">• set</span>}
                </span>
                <span className="text-xs text-bw-muted">{showIntention ? 'Hide' : 'Edit'}</span>
              </button>
              {showIntention && (
                <div className="px-4 pb-3">
                  <p className="text-xs text-bw-muted mb-1.5">
                    Your guiding principles for the build (architecture preferences, must-use stack, non-negotiables,
                    delivery philosophy). This is treated as <strong>authoritative</strong> and steers the chat and every Build Brief.
                  </p>
                  <textarea
                    value={intention}
                    onChange={(e) => { setIntention(e.target.value); setDirty(true); }}
                    onBlur={() => { if (dirty) saveHandoff(undefined, { silent: true }); }}
                    placeholder={'e.g. "Build as a static HTML5/CSS/JS site (no backend); WCAG 2.1 AA is non-negotiable; deploy to cloud.gov; favor boring, well-supported tech; every requirement traces to an acceptance test."'}
                    rows={4}
                    className="w-full resize-y rounded-lg border border-bw-border dark:border-gray-600 bg-bw-surface dark:bg-gray-800 px-3 py-2 text-sm text-bw-ink dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-bw-surface-subtle dark:bg-gray-950">
              {messages.length === 0 && (
                <div className="text-sm text-bw-muted bg-bw-surface dark:bg-gray-900 border border-bw-border dark:border-gray-700 rounded-lg p-4">
                  <p className="font-medium text-bw-ink dark:text-gray-300 mb-1">Refine the build before generating.</p>
                  <p>Ask the AI things like: <em>"What architecture fits the 508 + ATO requirements?"</em>,
                  {' '}<em>"Break the LMS integration into services"</em>, or <em>"What's the minimal stack to satisfy the SOW?"</em>
                  {' '}When ready, hit <strong>Generate Build Brief</strong>. You can also generate immediately and refine after.</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {m.role === 'user' ? (
                    <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap bg-bw-primary text-white">
                      {m.content}
                    </div>
                  ) : (
                    <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-bw-surface dark:bg-gray-800 text-bw-ink dark:text-gray-100 border border-bw-border dark:border-gray-700 prose prose-sm prose-slate dark:prose-invert max-w-none prose-p:my-1 prose-headings:mt-2 prose-headings:mb-1 prose-ul:my-1 prose-ol:my-1">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-bw-surface dark:bg-gray-800 border border-bw-border dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-bw-muted flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-bw-border dark:border-gray-700">
              {/* Grounding status + attachments */}
              <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="inline-flex items-center gap-1 text-bw-muted">
                  {docsLoading ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading solicitation documents…</>
                  ) : solicitationDocs.length ? (
                    <><FileText className="h-3.5 w-3.5 text-success" /> Grounded on {solicitationDocs.length} solicitation document{solicitationDocs.length > 1 ? 's' : ''}</>
                  ) : (
                    <><FileText className="h-3.5 w-3.5" /> No solicitation documents found — attach the PWS/SOW below</>
                  )}
                </span>
                {attachments.map((a) => (
                  <span key={a.name} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${a.status === 'error' ? 'border-red-300 text-red-600' : 'border-bw-border text-bw-ink dark:text-gray-200'}`}>
                    {a.status === 'extracting' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                    <span className="max-w-[140px] truncate">{a.name}</span>
                    <button onClick={() => removeAttachment(a.name)} className="hover:text-red-600"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                <input ref={fileInputRef} type="file" multiple className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md,.csv,.json,.rtf,.log" onChange={handleAttach} />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="inline-flex items-center gap-1 rounded-full border border-bw-border px-2 py-0.5 text-bw-primary hover:bg-bw-surface-subtle dark:hover:bg-gray-800 disabled:opacity-50">
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />} Attach
                </button>
              </div>
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  placeholder="Refine the build direction… (Enter to send, Shift+Enter for newline)"
                  rows={2}
                  className="flex-1 resize-none rounded-lg border border-bw-border dark:border-gray-600 bg-bw-surface dark:bg-gray-800 px-3 py-2 text-sm text-bw-ink dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={sendChat}
                  disabled={sending || !input.trim()}
                  className="px-3 self-stretch bg-bw-primary hover:brightness-110 disabled:opacity-50 text-white rounded-lg flex items-center"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={generate}
                disabled={generating}
                className="mt-2 w-full px-4 py-2 bg-bw-primary text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating Build Brief…</> : <><Sparkles className="h-4 w-4" /> Generate Build Brief</>}
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {!brief ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-bw-muted gap-3">
                <FileCode2 className="h-10 w-10 text-gray-300" />
                <p className="text-sm max-w-sm">No brief yet. Refine in the chat (optional), then click <strong>Generate Build Brief</strong>.</p>
                <button onClick={generate} disabled={generating} className="px-4 py-2 bg-bw-primary text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                  {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate Build Brief</>}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingBrief((v) => !v)}
                      className={`px-3 py-1.5 text-xs font-medium border rounded-lg flex items-center gap-1.5 ${editingBrief ? 'border-bw-primary text-bw-primary bg-bw-primary/5' : 'border-bw-border dark:border-gray-600 hover:bg-bw-surface-subtle dark:hover:bg-gray-800'}`}
                      title={editingBrief ? 'Preview the brief' : 'Edit the brief'}
                    >
                      {editingBrief ? <><Eye className="h-3.5 w-3.5" /> Preview</> : <><Pencil className="h-3.5 w-3.5" /> Edit</>}
                    </button>
                    <button
                      onClick={() => setShowVersions((v) => !v)}
                      className={`px-3 py-1.5 text-xs font-medium border rounded-lg flex items-center gap-1.5 ${showVersions ? 'border-bw-primary text-bw-primary bg-bw-primary/5' : 'border-bw-border dark:border-gray-600 hover:bg-bw-surface-subtle dark:hover:bg-gray-800'}`}
                      title="Version history"
                    >
                      <History className="h-3.5 w-3.5" /> Versions{versions.length ? ` (${versions.length})` : ''}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => copy('brief', brief)} className="px-3 py-1.5 text-xs font-medium border border-bw-border dark:border-gray-600 rounded-lg flex items-center gap-1.5 hover:bg-bw-surface-subtle dark:hover:bg-gray-800">
                      {copied === 'brief' ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />} Copy brief
                    </button>
                    <button onClick={downloadBrief} className="px-3 py-1.5 text-xs font-medium border border-bw-border dark:border-gray-600 rounded-lg flex items-center gap-1.5 hover:bg-bw-surface-subtle dark:hover:bg-gray-800">
                      <Download className="h-3.5 w-3.5" /> Download .md
                    </button>
                    <button onClick={generate} disabled={generating} className="px-3 py-1.5 text-xs font-medium border border-bw-border dark:border-gray-600 rounded-lg flex items-center gap-1.5 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 disabled:opacity-60">
                      {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Regenerate
                    </button>
                  </div>
                </div>

                {dirty && (
                  <p className="text-xs text-fit-moderate -mt-2">You have unsaved edits — click <strong>Save</strong> (top right) to keep them.</p>
                )}

                {/* Version history panel */}
                {showVersions && (
                  <div className="border border-bw-border dark:border-gray-700 rounded-lg p-3 bg-bw-surface-subtle dark:bg-gray-950">
                    <h3 className="text-sm font-bold text-bw-ink dark:text-white mb-2 flex items-center gap-1.5">
                      <History className="h-4 w-4 text-bw-primary" /> Version History
                    </h3>
                    {versions.length === 0 ? (
                      <p className="text-xs text-bw-muted">No saved versions yet. Each time you generate a brief, a snapshot is saved here so you never lose a prior version.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {versions.map((v) => (
                          <li key={v.id} className="flex items-center justify-between gap-2 text-xs bg-bw-surface dark:bg-gray-900 border border-bw-border dark:border-gray-700 rounded-md px-2.5 py-1.5">
                            <span className="text-bw-ink dark:text-gray-200 truncate">
                              {v.label || new Date(v.created_at).toLocaleString()}
                              <span className="text-bw-muted"> · {Array.isArray(v.agent_prompts) ? v.agent_prompts.length : 0} prompts</span>
                            </span>
                            <span className="flex items-center gap-1.5 shrink-0">
                              <button onClick={() => restoreVersion(v)} className="text-bw-primary font-medium flex items-center gap-1 hover:underline" title="Restore this version">
                                <RotateCcw className="h-3.5 w-3.5" /> Restore
                              </button>
                              <button onClick={() => deleteVersion(v.id)} className="text-urgent hover:text-red-700" title="Delete this version">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {editingBrief ? (
                  <textarea
                    value={brief}
                    onChange={(e) => { setBrief(e.target.value); setDirty(true); }}
                    rows={20}
                    className="w-full resize-y rounded-lg border border-bw-border dark:border-gray-600 bg-bw-surface dark:bg-gray-900 p-4 text-sm font-mono text-bw-ink dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                ) : (
                  <div className="prose prose-sm prose-slate dark:prose-invert max-w-none border border-bw-border dark:border-gray-700 rounded-lg p-4 bg-bw-surface dark:bg-gray-900">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{brief}</ReactMarkdown>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-bw-ink dark:text-white flex items-center gap-1.5">
                      <FileCode2 className="h-4 w-4 text-bw-primary" /> Agent Prompts ({agentPrompts.length})
                    </h3>
                    <button onClick={addPrompt} className="text-xs font-medium text-bw-primary hover:underline flex items-center gap-1">
                      + Add prompt
                    </button>
                  </div>
                  <div className="space-y-3">
                    {agentPrompts.map((p, i) => (
                      <div key={i} className="border border-bw-border dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-bw-surface-subtle dark:bg-gray-800">
                          <span className="text-xs text-bw-muted shrink-0">{i + 1}.</span>
                          <input
                            value={p.title}
                            onChange={(e) => updatePrompt(i, { title: e.target.value })}
                            placeholder="Prompt title"
                            className="flex-1 bg-transparent text-sm font-medium text-bw-ink dark:text-gray-100 focus:outline-none border-b border-transparent focus:border-bw-primary"
                          />
                          <button onClick={() => copy(`p${i}`, p.prompt)} className="text-xs font-medium text-bw-primary flex items-center gap-1 shrink-0">
                            {copied === `p${i}` ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />} Copy
                          </button>
                          <button onClick={() => removePrompt(i)} className="text-urgent hover:text-red-700 shrink-0" title="Delete this prompt">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <textarea
                          value={p.prompt}
                          onChange={(e) => updatePrompt(i, { prompt: e.target.value })}
                          rows={6}
                          className="w-full resize-y text-xs text-bw-ink dark:text-gray-300 p-3 bg-bw-surface dark:bg-gray-900 font-mono whitespace-pre-wrap break-words focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    ))}
                    {agentPrompts.length === 0 && (
                      <p className="text-xs text-bw-muted">No agent prompts yet. Generate a brief or click "Add prompt".</p>
                    )}
                  </div>
                </div>

                {openQuestions.length > 0 && (
                  <div className="border border-amber-200 dark:border-amber-800 bg-fit-moderate-bg dark:bg-amber-900/20 rounded-lg p-3">
                    <h3 className="text-sm font-bold text-fit-moderate dark:text-amber-200 mb-2 flex items-center gap-1.5">
                      <HelpCircle className="h-4 w-4" /> Open Questions
                    </h3>
                    <ul className="list-disc list-inside text-sm text-fit-moderate dark:text-amber-200 space-y-1">
                      {openQuestions.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BuildHandoffDialog;
